import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { mlmAPI } from '../../services/api';
import { Wallet, DollarSign, History, RefreshCw, Send } from 'lucide-react';
import { toast } from '../../components/common/Toast';
import { getApiError } from '../../services/api';

const TABS = ['Wallet Balance', 'Commission History', 'Wallet Transactions', 'Cycle History'];

const WalletPage = () => {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [wallet, setWallet] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const handleWithdrawal = async () => {
    const amount = parseFloat(withdrawalAmount);
    if (!amount || amount <= 0) return toast.warning('Enter a valid amount');
    if (amount > (wallet?.balance || 0)) return toast.error('Insufficient balance');
    setWithdrawing(true);
    try {
      await mlmAPI.requestWithdrawal(amount);
      toast.success('Withdrawal request submitted');
      setWithdrawalAmount('');
      const walletRes = await mlmAPI.getWallet();
      setWallet(walletRes.data);
    } catch (err) {
      toast.error(getApiError(err, 'Withdrawal failed'));
    } finally {
      setWithdrawing(false);
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [walletRes, commRes, txRes, cycRes] = await Promise.all([
          mlmAPI.getWallet(),
          mlmAPI.getCommissionHistory(),
          mlmAPI.getWalletTransactions(),
          mlmAPI.getCycles(),
        ]);
        setWallet(walletRes.data);
        setCommissions(commRes.data?.results || commRes.data || []);
        setTransactions(txRes.data?.results || txRes.data || []);
        setCycles(cycRes.data?.results || cycRes.data || []);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const cardClass = `${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`;
  const tabClass = (isActive) =>
    `px-4 py-2.5 text-sm font-medium rounded-xl transition-all ${
      isActive
        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md'
        : `${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'}`
    }`;

  const Table = ({ data, cols }) => {
    if (!data || data.length === 0) {
      return <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No data available</p>;
    }
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {cols.map((col) => (
                <th key={col.key} className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={row.id || i} className={`border-b ${isDarkMode ? 'border-gray-700/50' : 'border-gray-100'}`}>
                {cols.map((col) => (
                  <td key={col.key} className={`py-3 px-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {col.render ? col.render(row) : row[col.key] ?? '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTab = () => {
    switch (activeTab) {
      case 0:
        return (
          <div className="space-y-4">
            <div className={cardClass}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Wallet Balance</h3>
                <div className="p-3 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                  <DollarSign className="w-6 h-6" />
                </div>
              </div>
              <p className={`text-4xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                ${wallet?.balance?.toFixed(2) || '0.00'}
              </p>
              <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Available for withdrawal</p>
            </div>

            <div className={cardClass}>
              <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Request Withdrawal</h3>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Amount (USD)</label>
                  <input
                    type="number"
                    value={withdrawalAmount}
                    onChange={(e) => setWithdrawalAmount(e.target.value)}
                    placeholder="0.00"
                    className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
                <button onClick={handleWithdrawal} disabled={withdrawing}
                  className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-medium hover:shadow-lg transition-shadow inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <Send className="w-4 h-4" /> {withdrawing ? 'Submitting...' : 'Submit'}
                </button>
              </div>
              <p className={`mt-2 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Withdrawal requests are processed manually. Minimum withdrawal: $10.00
              </p>
            </div>
          </div>
        );
      case 1:
        return (
          <div className={cardClass}>
            <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Commission History</h3>
            <Table
              data={commissions}
              cols={[
                { key: 'date', label: 'Date', render: (r) => (r.created_at || r.date) ? new Date(r.created_at || r.date).toLocaleDateString() : '—' },
                { key: 'type', label: 'Type' },
                { key: 'amount', label: 'Amount', render: (r) => <span className="text-green-500 font-medium">${r.amount?.toFixed(2)}</span> },
                { key: 'description', label: 'Description' },
                { key: 'status', label: 'Status', render: (r) => (
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${r.status === 'paid' ? 'bg-green-100 text-green-800' : r.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>
                    {r.status}
                  </span>
                )},
              ]}
            />
          </div>
        );
      case 2:
        return (
          <div className={cardClass}>
            <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Wallet Transactions</h3>
            <Table
              data={transactions}
              cols={[
                { key: 'created_at', label: 'Date', render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : '—' },
                { key: 'type', label: 'Type', render: (r) => (
                  <span className={`capitalize ${r.type === 'credit' ? 'text-green-500' : 'text-red-500'}`}>{r.type}</span>
                )},
                { key: 'amount', label: 'Amount', render: (r) => (
                  <span className={`font-medium ${r.type === 'credit' ? 'text-green-500' : 'text-red-500'}`}>
                    {r.type === 'credit' ? '+' : '-'}${Math.abs(r.amount)?.toFixed(2)}
                  </span>
                )},
                { key: 'description', label: 'Description' },
              ]}
            />
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            {cycles.length > 0 ? cycles.map((cycle, i) => (
              <div key={cycle.id || i} className={cardClass}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Cycle #{cycle.cycle_number || cycle.id}</h3>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {cycle.start_date ? new Date(cycle.start_date).toLocaleDateString() : ''} - {cycle.end_date ? new Date(cycle.end_date).toLocaleDateString() : ''}
                    </p>
                  </div>
                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                    cycle.is_processed ? 'bg-green-100 text-green-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {cycle.is_processed ? 'Completed' : 'Pending'}
                  </span>
                </div>
                {cycle.commission_earned > 0 && (
                  <p className="mt-2 text-sm">
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Commission Earned: </span>
                    <span className="font-medium text-green-500">${cycle.commission_earned?.toFixed(2)}</span>
                  </p>
                )}
              </div>
            )) : (
              <div className={cardClass}>
                <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No cycle history available</p>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div>
        <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Wallet & Commissions</h1>
        <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Manage your earnings and view history</p>
      </div>

      <div className={`flex flex-wrap gap-2 p-1 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-2xl`}>
        {TABS.map((tab, i) => (
          <button key={tab} onClick={() => setActiveTab(i)} className={tabClass(activeTab === i)}>
            {tab}
          </button>
        ))}
      </div>

      {renderTab()}
    </motion.div>
  );
};

export default WalletPage;
